import React, {useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getCallToBook} from 'modules/search/selectors'

import {Link} from '@vio/atlas/Link'
import {NoStyleButton} from '@vio/atlas/src/components/designSystem/helpers/NoStyleButton'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useToastController} from '@vio/atlas/src/context/toastController'
import {cssTheme} from '@vio/atlas/src/themes'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {logger} from '@vio/core/src/utils/logging/logger'

import {getEventPayload} from './useCallToBook'

const SOURCE_COMPONENT = 'srp_anchor'

const ButtonContainer = styled(NoStyleButton)`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
  color: ${cssTheme.colors.content.brand.primary};
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s600};
`
const LinkContainer = styled(Link)`
  background-color: ${cssTheme.colors.background.brand.tertiary};
  padding: ${cssTheme.layout.spacing.s400};
  border-top: 1px solid;
  border-color: ${cssTheme.colors.border.brand.tertiary};
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none !important;
  & > span {
    color: ${cssTheme.colors.content.brand.primary};
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${cssTheme.layout.spacing.s200};
  }
  .--phone-number {
    text-decoration: underline;
  }
`
interface Props {
  isMobile?: boolean
}
// Added as part of 27d5ef4a-call-to-book-anchor experiment
export const CallToBookAnchor = ({isMobile}: Props) => {
  const dispatch = useDispatch()
  const toggle = useToggle()
  const toast = useToastController()

  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.2})
  const {status, phoneNumber, displayedPhoneNumber, href, payload} =
    useSelector(getCallToBook) ?? {}
  const hasTrackedView = useRef(false)

  const handleClick = async (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e?.preventDefault()
    e?.stopPropagation()

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.CallToBook,
        action: Action.Clicked,
        page: Page.Search,
        payload: getEventPayload({
          payload,
          displayedPhoneNumber,
          component: SOURCE_COMPONENT
        })
      })
    )

    if (isMobile) return

    try {
      await navigator.clipboard.writeText(phoneNumber)
      toast.open(
        <FormattedMessageWrapper
          id="callToBook.phoneNumberCopied"
          defaultMessage="Phone number copied."
        />,
        {
          icon: <Icon name="Checkmark" size="sm" />,
          autoClose: true,
          closeOnOutsideClick: true,
          style: {width: 'fit-content', minWidth: 'auto'}
        }
      )
    } catch (error) {
      logger.error('Failed to copy:', error)
    }
  }

  useEffect(() => {
    if (!inView || hasTrackedView.current) return

    hasTrackedView.current = true

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.CallToBook,
        action: Action.Viewed,
        page: Page.Search,
        payload: getEventPayload({
          payload,
          displayedPhoneNumber,
          component: SOURCE_COMPONENT
        })
      })
    )
  }, [inView, dispatch, payload, displayedPhoneNumber])

  if (
    toggle(
      '27d5ef4a-call-to-book-anchor',
      true,
      status !== 'ready' || !phoneNumber
    )
  )
    return null

  if (isMobile) {
    return (
      <LinkContainer href={href} onClick={() => handleClick()} ref={ref}>
        <Icon name="Phone" size="sm" />
        <Text variant="labelS">
          <FormattedMessageWrapper
            id="callToBook.bookByPhone"
            defaultMessage="Book by phone: {phoneNumber}"
            values={{
              phoneNumber: <span className="--phone-number">{phoneNumber}</span>
            }}
          />
        </Text>
      </LinkContainer>
    )
  }

  return (
    <ButtonContainer onClick={handleClick} ref={ref}>
      <Icon name="Phone" size="sm" />
      <Text variant="labelS">
        <FormattedMessageWrapper
          id="callToBook.bookByPhone"
          defaultMessage="Book by phone: {phoneNumber}"
          values={{
            phoneNumber
          }}
        />
      </Text>
      <Icon name="Copy" size="sm" />
    </ButtonContainer>
  )
}
